import { Capacitor } from '@capacitor/core'

const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1'
const timeoutMs = Number(import.meta.env.VITE_API_TIMEOUT || 30000)

function getToken() {
  try {
    // Try multiple possible token keys to ensure compatibility
    return localStorage.getItem('access_token') || 
           localStorage.getItem('token') || 
           ''
  } catch {
    return ''
  }
}

function getTenantId() {
  try {
    return localStorage.getItem('tenantId') || ''
  } catch {
    return ''
  }
}

async function request(path, options = {}) {
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(new Error(`Request timed out after ${timeoutMs}ms. Pastikan server API aktif.`)), timeoutMs)
  const headers = new Headers(options.headers || {})
  const token = getToken()
  const tenantId = getTenantId()
  
  // Debug logging for admin requests
  if (path.includes('/admin')) {
    console.log('=== Admin API Request ===');
    console.log('Path:', path);
    console.log('Has Token:', !!token);
    console.log('Token length:', token?.length || 0);
    console.log('Has Tenant ID:', !!tenantId);
  }
  
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (tenantId) headers.set('X-Tenant-ID', tenantId)
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
  if (!isFormData) {
    headers.set('Content-Type', 'application/json')
  }
  
  // On native platform, log outgoing requests for debugging
  if (Capacitor.isNativePlatform()) {
    console.log(`[API] ${options.method || 'GET'} ${path}`, { hasToken: !!token, hasTenant: !!tenantId });
  }

  try {
    const res = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers,
      signal: controller.signal
    })
    clearTimeout(id)
    const contentType = res.headers.get('content-type') || ''
    const isJson = contentType.includes('application/json')
    const body = isJson ? await res.json() : await res.text()
    
    if (!res.ok) {
      console.error('API Error Response:', {
        status: res.status,
        statusText: res.statusText,
        body: body,
        path: path
      });

      if (res.status === 401) {
        const authKeys = [
          'access_token', 'token', 'user_data', 'selectedTenant',
          'tenantId', 'tenantName', 'tenantSlug', 'username', 'email', 'avatar_url'
        ]
        authKeys.forEach(key => localStorage.removeItem(key))
        // Use replace on origin + /login to stay within the SPA context
        // This works correctly in both web browsers and Capacitor webview
        window.location.replace(window.location.origin + '/login')
        return
      }
      
      const message = isJson ? body.message || body.error || 'Request failed' : String(body || 'Request failed')
      throw new Error(message)
    }
    return body
  } catch (err) {
    clearTimeout(id)
    console.error('API Request Failed:', err);
    throw err
  }
}

async function requestBlob(path, options = {}) {
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(new Error(`Request timed out after ${timeoutMs}ms. Pastikan server API aktif.`)), timeoutMs)
  const headers = new Headers(options.headers || {})
  const token = getToken()
  const tenantId = getTenantId()

  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (tenantId) headers.set('X-Tenant-ID', tenantId)

  try {
    const res = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    })
    clearTimeout(id)
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `Request failed with status ${res.status}`)
    }
    return res.blob()
  } catch (err) {
    clearTimeout(id)
    throw err
  }
}

export const apiClient = {
  get: (path) => request(path, { method: 'GET' }),
  getBlob: (path) => requestBlob(path, { method: 'GET' }),
  post: (path, body) => request(path, { method: 'POST', body: JSON.stringify(body) }),
  postForm: (path, formData) => request(path, { method: 'POST', body: formData }),
  put: (path, body) => request(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: (path, body) => request(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (path, body) => request(path, { method: 'DELETE', ...(body === undefined ? {} : { body: JSON.stringify(body) }) })
}

